/* eslint-disable camelcase */
// @ts-check

/**
 * @param {Array<Vendor>} cvl
 * @returns {NonIABVendorList}
 */
const parseVendors = (cvl) => {
  return cvl.reduce(
    (acc, { provider_id, provider_name, policy_url }) => ({
      ...acc,
      [provider_id]: {
        id: provider_id,
        name: provider_name,
        policyUrl: policy_url
      }
    }),
    {}
  )
}

export class NonIABModel {
  /**
   * @type {Consents}
   */
  #consents

  /**
   * @type {string}
   */
  #consentString

  /**
   * @type {NonIABVendorList}
   */
  #cvl

  /**
   * @type {number|string}
   */
  #vendorListVersion

  /**
   * @type {Array<number>}
   */
  #vendorIds

  /**
   * @param {Object} opts
   * @param {Array<Vendor>} opts.vendorList
   * @param {string} [opts.consentString]
   * @param {number|string} opts.vendorListVersion
   */
  constructor({ vendorList, consentString, vendorListVersion }) {
    /**
     * @type {NonIABVendorList}
     */
    this.#cvl = parseVendors(vendorList)
    this.#vendorIds = Object.keys(this.#cvl).map((id) => parseInt(id))
    const vendorIds = new Set(this.#vendorIds)
    this.#consents = {}
    this.updateModel = this.updateModel.bind(this)
    this.getConsents = this.getConsents.bind(this)
    this.#consentString = `1~${
      consentString
        ? consentString
            .substr(2)
            .split('.')
            .map((id) => parseInt(id))
            .filter((id) => id)
            .filter((id) => vendorIds.has(id))
            .join('.')
        : ''
    }`
    this.#consents = this.#decode()
    this.#vendorListVersion = vendorListVersion
  }

  #encode() {
    const isEmpty = (obj) => !Object.keys(obj).length
    if (isEmpty(this.#consents)) return '1~'

    const ids = Object.entries(this.#consents)
      .filter(([ignore, { consent }]) => consent)
      .map(([id, ignore]) => id)
      .join('.')
    return `1~${ids}`
  }

  #decode() {
    if (this.#consentString === '1~') return {}

    const consentedVendorIds = this.#consentString
      .substr(2)
      .split('.')
      .map((id) => parseInt(id))
      .filter((id) => id)
    const consentMap = consentedVendorIds.reduce(
      (acc, id) => ({ ...acc, ...{ [id]: { consent: true } } }),
      {}
    )
    return consentMap
  }

  updateModel(consents) {
    this.#consents = consents
    this.#consentString = this.#encode()
  }

  getConsents() {
    return this.#consents
  }

  getConsentString() {
    return this.#consentString
  }

  consentToAll() {
    this.#consents = Object.keys(this.#cvl).reduce(
      (acc, id) => ({ ...acc, [id]: { consent: true } }),
      {}
    )
    this.#consentString = this.#encode()
  }

  dissentToAll() {
    this.#consents = {}
    this.#consentString = this.#encode()
  }

  hasUserConsentedToAll() {
    const cvlIds = Object.keys(this.#cvl)
    return cvlIds.every(
      (id) => this.#consents[id] && this.#consents[id].consent
    )
  }

  hasUserDissentedToAll() {
    const cvlIds = Object.keys(this.#cvl)
    return cvlIds.every(
      (id) => !(this.#consents[id] && this.#consents[id].consent)
    )
  }

  /**
   * @returns {UiModel}
   */
  getUiModel() {
    return {
      cvl: this.#cvl,
      consents: this.getConsents(),
      vendorIds: this.#vendorIds
    }
  }

  getVendors() {
    return this.#cvl
  }

  getVendorListVersion() {
    return this.#vendorListVersion
  }
}

/**
 * @typedef {Object} UiModel
 * @property {NonIABVendorList} cvl
 * @property {Consents} consents
 * @property {Array<number>} vendorIds
 */

/** @typedef {Record<number,{consent: boolean}>} Consents */

/**
 * @typedef {Object} Vendor
 * @property {string} provider_name
 * @property {string} provider_id
 * @property {string} policy_url
 */

/** @typedef {Record<string, Vendor>} NonIABVendorList */
